import { legalRoster } from "../trade/legalRoster.ts";
import { createRosterScoreCache } from "./rosterComposition.baseball.ts";
import {
	packageWeights,
	jointFits, portfolio, redundancy, competitiveIndex, immediateImpact, coreScore, holdMultiplier, pickPremiumCap, contenderProspectFactor, fillerFloors,
	type PositionProfile,
} from "../trade/packageValue.ts";
import {
	contractContext,
	loadContractTeamContext,
	type ContractContext,
	type ContractTeamContext,
} from "../trade/contractContext.ts";
import { organizationPlayers } from "../trade/organizationPlayers.ts";
import { affinity, affinityFactor, currentTid, loadWealth } from "../trade/tradeAffinity.ts";
import { PHASE, PLAYER } from "../../../common/constants.ts";
import { draft, player, team, trade } from "../index.ts";
import { idb } from "../../db/index.ts";
import { g, helpers, local } from "../../util/index.ts";
import type {
	TradePickValues,
	Player,
	PlayerContract,
	PlayerInjury,
	DraftPick,
} from "../../../common/types.ts";
import { getNumPicksPerRound } from "../trade/getPickValues.ts";
import { bySport, isSport } from "../../../common/sportFunctions.ts";
import { groupByUnique, last } from "../../../common/utils.ts";

type Asset =
	| {
			type: "player";
			pid: number;
			talentValue: number;
			name: string;
			profile: PositionProfile;
			value: number;
			contractValue: number;
			injury: PlayerInjury;
			age: number;
			justDrafted: boolean;
			control?: ContractContext;
	  }
	| {
			type: "pick";
			value: number;
			contractValue: number;
			injury: PlayerInjury;
			age: number;
			dp: DraftPick;
			draftPick: number;
			draftYear: number;
	  };

const zscore = (value: number) =>
	(value - local.playerOvrMean) / local.playerOvrStd;

const ovrIndexToEstWinPercent = (teamOvrIndex: number) => {
	return (
		0.25 +
		(0.5 * (g.get("numActiveTeams") - 1 - teamOvrIndex)) /
			(g.get("numActiveTeams") - 1)
	);
};

const MIN_VALUE = bySport({
	baseball: -0.75,
	basketball: -0.5,
	football: -1,
	hockey: -0.5,
});
const MAX_VALUE = bySport({
	baseball: 2.5,
	basketball: 2,
	football: 3,
	hockey: 2,
});
const getContractValue = (
	contract: PlayerContract,
	normalizedValue: number,
) => {
	const season = g.get("season");
	const phase = g.get("phase");
	if (
		contract.exp === season ||
		(phase > PHASE.PLAYOFFS && contract.exp === season + 1)
	) {
		// Don't care about expiring contracts
		return 0;
	}

	const salaryCap = g.get("salaryCap");
	const normalizedContractAmount = contract.amount / salaryCap;

	const slope =
		(g.get("maxContract") / salaryCap - g.get("minContract") / salaryCap) /
		(MAX_VALUE - MIN_VALUE);

	const expectedAmount = slope * (normalizedValue - MIN_VALUE);

	const contractValue = expectedAmount - normalizedContractAmount;

	// Don't let contract value exceed 0.1, it's just a small boost or a big penalty
	return Math.min(contractValue, 0.1);
};

const getPlayers = async ({
	add,
	remove,
	roster,
	pidsAdd,
	pidsRemove,
	tid,
	tradingPartnerTid,
	getDisposition,
}: {
	add: Asset[];
	remove: Asset[];
	roster: Asset[];
	pidsAdd: number[];
	pidsRemove: number[];
	tid: number;
	tradingPartnerTid?: number;
	getDisposition: (
		p: Player,
		tid: number,
	) => Promise<ContractContext | undefined>;
}) => {
	const season = g.get("season");
	const phase = g.get("phase");
	const difficultyFudgeFactor = helpers.bound(
		1 + 0.1 * g.get("difficulty"),
		0,
		Infinity,
	); // 2.5% bonus for easy, 2.5% penalty for hard, 10% penalty for insane

	// Fudge factor for AI overvaluing its own players
	const fudgeFactor =
		(tid !== g.get("userTid") && tradingPartnerTid !== g.get("userTid")
			? 1.05
			: 1) * difficultyFudgeFactor;

	// Get roster and players to remove
	const players = await organizationPlayers(tid);

	for (const p of players) {
		const value = zscore(p.value);
		if (!pidsRemove.includes(p.pid)) {
			roster.push({
				type: "player",
				pid: p.pid,
				talentValue: value,
				name: `${p.firstName} ${p.lastName}`,
				profile: {
					pid: p.pid,
					age: season - p.born.year,
					ovrs: last(p.ratings).ovrs ?? {},
					pots: last(p.ratings).pots ?? {},
				},
				value,
				contractValue: getContractValue(p.contract, value),
				injury: p.injury,
				age: g.get("season") - p.born.year,
				justDrafted: helpers.justDrafted(p, phase, season),
			});
		} else {
			// Only apply fudge factor to positive assets
			let fudgedValue = value;
			if (fudgedValue > 0) {
				fudgedValue *= fudgeFactor;
			}

			remove.push({
				type: "player",
				pid: p.pid,
				talentValue: value,
				name: `${p.firstName} ${p.lastName}`,
				profile: {
					pid: p.pid,
					age: season - p.born.year,
					ovrs: last(p.ratings).ovrs ?? {},
					pots: last(p.ratings).pots ?? {},
				},
				control: await getDisposition(p, tid),
				value: fudgedValue,
				contractValue: getContractValue(p.contract, value),
				injury: p.injury,
				age: g.get("season") - p.born.year,
				justDrafted: helpers.justDrafted(p, phase, season),
			});
		}
	}

	// Get players to add
	for (const pid of pidsAdd) {
		const p = await idb.cache.players.get(pid);
		if (p) {
			const value = zscore(p.value);

			add.push({
				type: "player",
				control: await getDisposition(p, tid),
				pid: p.pid,
				talentValue: value,
				name: `${p.firstName} ${p.lastName}`,
				profile: {
					pid: p.pid,
					age: season - p.born.year,
					ovrs: last(p.ratings).ovrs ?? {},
					pots: last(p.ratings).pots ?? {},
				},
				value,
				contractValue: getContractValue(p.contract, value),
				injury: p.injury,
				age: g.get("season") - p.born.year,
				justDrafted: helpers.justDrafted(p, phase, season),
			});
		}
	}
};

const getPickNumber = async (
	cache: ValueChangeCache,
	dp: DraftPick,
	season: number,
	pidsAdd: number[],
	pidsRemove: number[],
	tid: number,
	tradingPartnerTid?: number,
	legalRosterOf?: (players: Player[]) => { ovr: number },
) => {
	const numPicksPerRound = getNumPicksPerRound();

	let estPick: number;
	// Projection with no user-specific adjustment (same for every organization). It still includes the effect of
	// the players moving in this trade, so it can't be gamed by bundling the pick with the team's best players.
	let universalEstPick: number;
	if (dp.pick > 0) {
		estPick = dp.pick;
		universalEstPick = dp.pick;
	} else {
		let temp = cache.estPicks[dp.originalTid];

		// Used to know when to overvalue own pick
		const tradeWithUser = tradingPartnerTid === g.get("userTid");

		// if trading with the user, make sure the AI pick is accurately judged
		// based on what players are outgoing in the trade
		// and just use the cached estimated pick if no players are being exchanged
		if (
			tid !== g.get("userTid") &&
			dp.originalTid === tid &&
			tradeWithUser &&
			pidsAdd.length + pidsRemove.length > 0
		) {
			temp = await getModifiedPickRank(
				cache,
				tid,
				pidsAdd,
				pidsRemove,
				legalRosterOf,
			);
		}
		estPick = temp !== undefined ? temp : numPicksPerRound / 2;

		{
			let universalSeasons = helpers.bound(season - g.get("season"), 0, 5);
			if (universalSeasons === 0 && g.get("phase") < PHASE.PLAYOFFS) {
				universalSeasons += 0.5;
			}
			universalEstPick = Math.round(
				(estPick * (5 - universalSeasons)) / 5 +
					(0.25 * numPicksPerRound * universalSeasons) / 5,
			);
		}

		// tid rather than originalTid, because it's about what the user can control
		const usersPick = dp.tid === g.get("userTid");

		// For future draft picks, add some uncertainty.
		const regressionTarget = (usersPick ? 0.75 : 0.25) * numPicksPerRound;

		// Never let this improve the future projection of user's picks
		let seasons = helpers.bound(season - g.get("season"), 0, 5);
		if (tradeWithUser && seasons > 0) {
			// When trading with the user, expect things to change rapidly
			seasons = helpers.bound(seasons + 1, 0, 5);
		}

		if (seasons === 0 && g.get("phase") < PHASE.PLAYOFFS) {
			// Would be better to base on fraction of season completed, but oh well
			seasons += 0.5;
		}

		// Weighted average of estPicks and regressionTarget
		estPick = Math.round(
			(estPick * (5 - seasons)) / 5 + (regressionTarget * seasons) / 5,
		);

		if (tradeWithUser && seasons > 0) {
			if (usersPick) {
				// Penalty for user draft picks
				const difficultyFactor = 1 + 1.5 * g.get("difficulty");
				estPick = helpers.bound(
					Math.round((estPick + numPicksPerRound / 3.5) * difficultyFactor),
					1,
					numPicksPerRound,
				);
			} else {
				// Bonus for AI draft picks
				estPick = helpers.bound(
					Math.round(estPick - numPicksPerRound / 3.5),
					1,
					numPicksPerRound,
				);
			}
		}
	}

	estPick += numPicksPerRound * (dp.round - 1);
	universalEstPick += numPicksPerRound * (dp.round - 1);

	return { estPick, universalEstPick };
};

const getPickInfo = async (
	cache: ValueChangeCache,
	dp: DraftPick,
	rookieSalaries: any,
	pidsAdd: number[],
	pidsRemove: number[],
	tid: number,
	tradingPartnerTid?: number,
	legalRosterOf?: (players: Player[]) => { ovr: number },
): Promise<Asset> => {
	const season =
		dp.season === "fantasy" || dp.season === "expansion"
			? g.get("season")
			: dp.season;

	const { estPick, universalEstPick } = await getPickNumber(
		cache,
		dp,
		season,
		pidsAdd,
		pidsRemove,
		tid,
		tradingPartnerTid,
		legalRosterOf,
	);

	const valueAt = (est: number) => {
		let value;
		const valuesTemp = cache.estValues[season];
		if (valuesTemp) {
			value = valuesTemp[est - 1];
		}
		if (value === undefined) {
			value = cache.estValues.default[est - 1];
		}
		if (value === undefined) {
			value = cache.estValues.default.at(-1);
		}
		if (value === undefined) {
			value = 20;
		}
		return zscore(value);
	};

	let value = valueAt(estPick);

	// Baseball: the AI's bonus on its own picks when dealing with the user is capped relative to the universal value,
	// in objective space (objectiveValue). User pick penalties and difficulty are untouched.
	const userTid = g.get("userTid");
	if (
		isSport("baseball") &&
		tradingPartnerTid === userTid &&
		tid !== userTid &&
		dp.tid !== userTid &&
		dp.pick <= 0
	) {
		const universal = Math.max(MIN_PICK_VALUE(), valueAt(universalEstPick));
		const roundPosition =
			universalEstPick - getNumPicksPerRound() * (dp.round - 1);
		const cap = inverseObjectiveValue(pickPremiumCap(roundPosition) * objectiveValue(universal));
		value = Math.min(value, cap);
	}

	let contractValue = getContractValue(
		{
			amount: rookieSalaries[estPick - 1],
			exp: season + 2,
		},
		value,
	);

	// Since rookies can be cut after the draft, value of a draft pick can't be negative
	value = Math.max(MIN_PICK_VALUE(), value);
	contractValue = Math.max(0, contractValue);

	// Ensure there are no tied pick values
	value -= estPick * 1e-10;

	return {
		type: "pick",
		value,
		contractValue,
		injury: {
			type: "Healthy",
			gamesRemaining: 0,
		},
		dp,

		// Would be better to store age in estValues, but oh well
		age: 20,
		draftPick: estPick,
		draftYear: season,
	};
};

const getPicks = async ({
	cache,
	add,
	remove,
	pidsAdd,
	pidsRemove,
	dpidsAdd,
	dpidsRemove,
	tid,
	tradingPartnerTid,
	legalRosterOf,
}: {
	cache: ValueChangeCache;
	add: Asset[];
	remove: Asset[];
	pidsAdd: number[];
	pidsRemove: number[];
	dpidsAdd: number[];
	dpidsRemove: number[];
	tid: number;
	tradingPartnerTid?: number;
	legalRosterOf?: (players: Player[]) => { ovr: number };
}) => {
	// For each draft pick, estimate its value based on the recent performance of the team
	if (dpidsAdd.length > 0 || dpidsRemove.length > 0) {
		const rookieSalaries = draft.getRookieSalaries();

		for (const dpid of dpidsAdd) {
			const dp = await idb.cache.draftPicks.get(dpid);
			if (!dp) {
				continue;
			}

			const pickInfo = await getPickInfo(
				cache,
				dp,
				rookieSalaries,
				pidsAdd,
				pidsRemove,
				tid,
				tradingPartnerTid,
				legalRosterOf,
			);
			add.push(pickInfo);
		}

		for (const dpid of dpidsRemove) {
			const dp = await idb.cache.draftPicks.get(dpid);
			if (!dp) {
				continue;
			}

			const pickInfo = await getPickInfo(
				cache,
				dp,
				rookieSalaries,
				pidsAdd,
				pidsRemove,
				tid,
				tradingPartnerTid,
				legalRosterOf,
			);
			remove.push(pickInfo);
		}

		// Be wary about giving away too many 1st round draft picks!
		if (remove.length > 0 && !isSport("baseball")) {
			// More value for individual players in basketball, similar to EXPONENT
			const SPORT_FACTOR = bySport({
				baseball: 2.5,
				basketball: 5,
				hockey: 2.5,
				football: 2.5,
			});

			const firstRoundPicks = [];
			const otherPicks = [];
			for (const asset of remove) {
				if (asset.type === "pick") {
					if (asset.dp.round === 1) {
						firstRoundPicks.push(asset);
					} else {
						otherPicks.push(asset);
					}
				}
			}

			// If there are more than 2 picks in the trade, make them a bit more valuable to the AI
			const numBeyond2 = firstRoundPicks.length - 2;
			if (numBeyond2 > 0) {
				for (const pick of firstRoundPicks) {
					if (pick.value > 0) {
						pick.value *= 1 + numBeyond2 / SPORT_FACTOR;
					}
				}
			}

			// Similar but less extreme for other picks
			const numBeyond2Other = otherPicks.length - 2;
			if (numBeyond2Other > 0) {
				for (const pick of otherPicks) {
					if (pick.value > 0) {
						pick.value *= 1 + numBeyond2 / (SPORT_FACTOR * pick.dp.round);
					}
				}
			}
		}
	}
};

const EXPONENT = bySport({
	baseball: 3,
	basketball: 7,
	football: 3,
	hockey: 3.5,
});

// Baseball counts value from replacement level, like WAR: z = -1 (about a replacement player) is worth 0, an average
// regular 1, and stars get a moderate premium, (z + 1) ** 1.5. With the other sports' curve (z ** 3 above the mean,
// linear and squashed below it) a 74 catcher was worth 14x a 63 starter and good regulars were worth almost nothing,
// so no package could buy a star. Below replacement level (or with a contract that eats his value) a player is worth
// less than nothing, linearly, but never below -1.
const BASEBALL_REPLACEMENT = 1;
// Smallest draft pick value: just above nothing (in baseball, just above replacement level)
const MIN_PICK_VALUE = () => (isSport("baseball") ? 0.1 - BASEBALL_REPLACEMENT : 0.1);
const BASEBALL_EXPONENT = 1.5;
const BASEBALL_MIN_VALUE = -1;
const objectiveValue = (x: number) => {
	if (isSport("baseball")) {
		const above = x + BASEBALL_REPLACEMENT;
		return above > 0 ? above ** BASEBALL_EXPONENT : Math.max(above, BASEBALL_MIN_VALUE);
	}
	return x > 1 ? x ** EXPONENT : x;
};
const inverseObjectiveValue = (y: number) => {
	if (isSport("baseball")) {
		return (y > 0 ? y ** (1 / BASEBALL_EXPONENT) : y) - BASEBALL_REPLACEMENT;
	}
	return y > 1 ? y ** (1 / EXPONENT) : y;
};

const sumValues = (
	players: Asset[],
	strategy: string,
	tid: number,
	includeInjuries = false,
) => {
	if (players.length === 0) {
		return 0;
	}

	const season = g.get("season");
	const phase = g.get("phase");

	return players.reduce((memo, p) => {
		let playerValue = p.value;

		const treatAsFutureDraftPick =
			p.type === "pick" && (season !== p.draftYear || phase <= PHASE.PLAYOFFS);

		// These factors don't make sense for negative value players!!!
		if (strategy === "rebuilding") {
			// Value young/cheap players and draft picks more. Penalize expensive/old players
			if (treatAsFutureDraftPick) {
				playerValue *= 1.1;
			} else if (p.age <= 19) {
				playerValue *= 1.075;
			} else if (p.age === 20) {
				playerValue *= 1.05;
			} else if (p.age === 21) {
				playerValue *= 1.0375;
			} else if (p.age === 22) {
				playerValue *= 1.025;
			} else if (p.age === 23) {
				playerValue *= 1.0125;
			} else if (p.age === 27) {
				playerValue *= 0.975;
			} else if (p.age === 28) {
				playerValue *= 0.95;
			} else if (p.age >= 29) {
				playerValue *= 0.9;
			}
		} else if (strategy === "contending") {
			// Much of the value for these players comes from potential, which we don't really care about
			if (treatAsFutureDraftPick) {
				playerValue *= 0.825;
			} else if (p.age <= 19) {
				playerValue *= 0.8;
			} else if (p.age === 20) {
				playerValue *= 0.825;
			} else if (p.age === 21) {
				playerValue *= 0.85;
			} else if (p.age === 22) {
				playerValue *= 0.875;
			} else if (p.age === 23) {
				playerValue *= 0.925;
			} else if (p.age === 24) {
				playerValue *= 0.95;
			}
		}

		// Normalize for injuries
		if (includeInjuries && tid !== g.get("userTid")) {
			if (p.injury.gamesRemaining > 75) {
				playerValue -= playerValue * 0.75;
			} else {
				playerValue -= (playerValue * p.injury.gamesRemaining) / 100;
			}
		}

		// Really bad players will just get no PT, but don't to count them as 0 because then AI thinks it can't find a trade.
		// Baseball measures from replacement level instead (objectiveValue)
		if (playerValue < 0 && !isSport("baseball")) {
			playerValue /= 20;
		}

		const contractsFactor = strategy === "rebuilding" ? 2 : 0.5;
		playerValue += contractsFactor * p.contractValue;

		// if a player was just drafted and can be released, they shouldn't have negative value
		if (p.type == "player" && p.justDrafted) {
			playerValue = Math.max(0, playerValue);
		}

		return memo + objectiveValue(playerValue);
	}, 0);
};

export const getEstPicks = async (
	teamOvrsSorted: { ovr: number; tid: number }[],
) => {
	const teams = (await idb.cache.teams.getAll()).filter((t) => !t.disabled);

	const allTeamSeasons = await idb.cache.teamSeasons.indexGetAll(
		"teamSeasonsBySeasonTid",
		[[g.get("season")], [g.get("season"), "Z"]],
	);

	let gp = 0;

	// Estimate the order of the picks by team
	const wps = teams.map((t) => {
		let teamOvrIndex = teamOvrsSorted.findIndex((t2) => t2.tid === t.tid);
		if (teamOvrIndex < 0) {
			// This happens if a team has no players on it - just assume they are the worst
			teamOvrIndex = teamOvrsSorted.length - 1;
		}

		// 25% to 75% based on rank
		const teamOvrWinp = ovrIndexToEstWinPercent(teamOvrIndex);

		const teamSeasons = allTeamSeasons.filter(
			(teamSeason) => teamSeason.tid === t.tid,
		);
		let record: [number, number];

		if (!teamSeasons[0]) {
			// Expansion team?
			record = [0, 0];
		} else {
			const teamSeason = teamSeasons[0];
			record = [teamSeason.won, teamSeason.lost];
		}

		gp = record[0] + record[1];

		const seasonFraction = gp / g.get("numGames");

		return {
			tid: t.tid,
			// Weighted average of current season record and team rating, based on how much of the current season is complete
			wp:
				gp === 0
					? teamOvrWinp
					: seasonFraction * (record[0] / gp) +
						(1 - seasonFraction) * teamOvrWinp,
		};
	});

	// Get rank order of wps http://stackoverflow.com/a/14834599/786644
	wps.sort((a, b) => a.wp - b.wp);

	// For each team, what is their estimated draft position?
	const estPicks: Record<number, number> = {};
	for (const [i, wp] of wps.entries()) {
		estPicks[wp.tid] = i + 1;
	}

	return {
		estPicks,
		wps,
	};
};

type ValueChangeCache = {
	estPicks: Record<number, number>;
	estValues: TradePickValues;
	teamOvrs: {
		tid: number;
		ovr: number;
	}[];
	wps: {
		tid: number;
		wp: number;
	}[];
};

type ToUpdate = {
	draft: boolean;

	// false - no update. "all" - update all teams. number[] = update only these specific teams
	teams: false | "all" | number[];
};

// tradingPartnerTid is currently just used to determine if this is a trade with the user, so additional fuzz can be applied
export class ValueChangeCalculator {
	private dispositions = new Map<string, Promise<ContractContext>>();
	private contractTeams = new Map<number, Promise<ContractTeamContext>>();
	private affinities = new Map<string, Promise<number>>();
	private wealth: Promise<Map<number, number>> | undefined;
	private getAffinity(p: Player, tid: number) {
		const key = `${p.pid}|${tid}`;
		let cached = this.affinities.get(key);
		if (!cached) {
			this.wealth ??= loadWealth();
			cached = this.wealth.then((wealth) => affinity(p, tid, wealth));
			this.affinities.set(key, cached);
		}
		return cached;
	}
	private getDisposition = (
		p: Player,
		tid: number,
	): Promise<ContractContext | undefined> => {
		if (!isSport("baseball")) return Promise.resolve(undefined);
		const key = `${tid}:${p.pid}`;
		let cached = this.dispositions.get(key);
		if (!cached) {
			let teamContext = this.contractTeams.get(tid);
			if (!teamContext) {
				teamContext = loadContractTeamContext(tid);
				this.contractTeams.set(tid, teamContext);
			}
			cached = teamContext.then(async (context) => contractContext(p, context, (await this.getCompetitive(tid)).C));
			this.dispositions.set(key, cached);
		}
		return cached;
	};
 private competitive = new Map<number,Promise<{C:number;strength:number;record:number;strategyScore:number;window:string}>>();
 private getCompetitive(tid:number) {
  let found=this.competitive.get(tid);
  if(!found){found=(async()=>{
   const t=(await idb.cache.teams.get(tid))!;
   const rows=this.cache!.teamOvrs;
   const rank=rows.findIndex(x=>x.tid===tid);
   const strength=rank<0?0:1-rank/Math.max(1,rows.length-1);
   const seasons=await idb.cache.teamSeasons.indexGetAll("teamSeasonsBySeasonTid",[[g.get("season")],[g.get("season"),"Z"]]);
   const records=this.cache!.teamOvrs.map(team=>{const row=seasons.find(r=>r.tid===team.tid);const gp=row?helpers.getTeamSeasonGp(row):0;
    return {tid:team.tid,wp:((row?.won??0)+20)/(gp+40)};});
   const wp=records.find(r=>r.tid===tid)?.wp??.5;
   const record=records.length>1?(records.filter(r=>r.wp<wp).length+.5*(records.filter(r=>r.wp===wp).length-1))/(records.length-1):.5;
   const strategyScore=t.strategy==='rebuilding'?0:Math.max(.25,Math.min(1,(strength+record)/2));
   const C=competitiveIndex(strategyScore,strength,record);
   return {C,strength,record,strategyScore,window:C>=.75?'Contender':C>=.5?'Playoff hopeful':C>=.25?'Middle':'Rebuilding'};
  })();this.competitive.set(tid,found);}return found;
 }
 private rosterCache = new Map<string,ReturnType<typeof legalRoster>>();
 // A player's slot scores depend only on his ratings/injury/age, so within one calculator they're computed once
 // instead of once per candidate roster (same assumption as rosterCache)
 private scoreOf = isSport("baseball") ? createRosterScoreCache(g.get("maxRosterSize"), g.get("dh") !== "none") : undefined;
 private virtualRoster(players:Player[]) {
  const key=players.map(p=>p.pid).sort((a,b)=>a-b).join(',');
  let result=this.rosterCache.get(key);if(!result){result=legalRoster(players,this.scoreOf);this.rosterCache.set(key,result);}return result;
 }
	private cache: ValueChangeCache | undefined;
	private toUpdate: ToUpdate = {
		draft: true,
		teams: "all",
	};

	private async init() {
		await player.updateOvrMeanStd();
		return this.getUpdatedCache();
	}

	private async getUpdatedCache() {
		const toUpdate = { ...this.toUpdate };
		this.toUpdate = { draft: false, teams: false };

		const estValues =
			toUpdate.draft || !this.cache
				? await trade.getPickValues()
				: this.cache.estValues;

		if (toUpdate.teams || !this.cache) {
			const playersByTid = Map.groupBy(
				(await idb.cache.players.getAll()).filter(p=>p.tid>=0 || (isSport("baseball") && p.tid===-9 && p.minorTid!==undefined)),
				(p) => p.tid===-9 ? p.minorTid! : p.tid,
			);
			const teamOvrs: {
				tid: number;
				ovr: number;
			}[] = [];
			let prevTeamOvrsByTid;
			for (const [tid, players] of playersByTid) {
				let row;
				if (
					this.cache &&
					Array.isArray(toUpdate.teams) &&
					!toUpdate.teams.includes(tid)
				) {
					if (!prevTeamOvrsByTid) {
						prevTeamOvrsByTid = groupByUnique(this.cache.teamOvrs, "tid");
					}
					row = prevTeamOvrsByTid[tid];
				}

				if (row === undefined) {
					const ovr = isSport("baseball") ? this.virtualRoster(players).ovr : team.ovr(
						players.map((p) => ({
							pid: p.pid,
							injury: p.injury,
							value: p.value,
							ratings: {
								ovr: last(p.ratings).ovr,
								ovrs: last(p.ratings).ovrs,
								pos: last(p.ratings).pos,
							},
						})),
					);

					row = { tid, ovr };
				}

				teamOvrs.push(row);
			}
			teamOvrs.sort((a, b) => b.ovr - a.ovr);

			const { estPicks, wps } = await getEstPicks(teamOvrs);

			return {
				estPicks,
				estValues,
				teamOvrs,
				wps,
			};
		} else {
			return {
				...this.cache,
				estValues,
			};
		}
	}

	invalidateCache(toUpdate: Partial<ToUpdate>) {
		this.dispositions.clear();
		this.competitive.clear();
		this.rosterCache.clear();
		this.contractTeams.clear();
		this.affinities.clear();
		this.wealth = undefined;
		// Merge with existing this.toUpdate - probably never matters vs overwriting, but just to be sure
		if (toUpdate.draft !== undefined) {
			this.toUpdate.draft = this.toUpdate.draft || toUpdate.draft;
		}
		if (toUpdate.teams === "all") {
			this.toUpdate.teams = "all";
		} else if (Array.isArray(toUpdate.teams)) {
			if (Array.isArray(this.toUpdate.teams)) {
				this.toUpdate.teams = Array.from(
					new Set([...this.toUpdate.teams, ...toUpdate.teams]),
				);
			} else if (this.toUpdate.teams === false) {
				this.toUpdate.teams = toUpdate.teams;
			}
		}
	}

	async evaluate(
		options: Parameters<ValueChangeCalculator["evaluateDetails"]>[0],
	): Promise<number> {
		return (await this.evaluateDetails(options)).net;
	}

	async evaluateDetails({
		tid,
		pidsAdd,
		pidsRemove,
		dpidsAdd,
		dpidsRemove,
		tradingPartnerTid,
	}: {
		tid: number;
		pidsAdd: number[];
		pidsRemove: number[];
		dpidsAdd: number[];
		dpidsRemove: number[];
		tradingPartnerTid: number | undefined;
	}) {
		if (!this.cache) {
			this.cache = await this.init();
		} else if (this.toUpdate.draft || this.toUpdate.teams) {
			this.cache = await this.getUpdatedCache();
		}

		pidsAdd=[...pidsAdd].sort((a,b)=>a-b); pidsRemove=[...pidsRemove].sort((a,b)=>a-b);
  dpidsAdd=[...dpidsAdd].sort((a,b)=>a-b); dpidsRemove=[...dpidsRemove].sort((a,b)=>a-b);
  // Get value and skills for each player on team or involved in the proposed transaction
		const roster: Asset[] = [];
		const add: Asset[] = [];
		const remove: Asset[] = [];
		const t = await idb.cache.teams.get(tid);

		if (!t) {
			throw new Error("Invalid team");
		}

		const strategy = t.strategy;

		await getPlayers({
			getDisposition: this.getDisposition,
			add,
			remove,
			roster,
			pidsAdd,
			pidsRemove,
			tid,
			tradingPartnerTid,
		});
		await getPicks({
			cache: this.cache,
			add,
			remove,
			pidsAdd,
			pidsRemove,
			dpidsAdd,
			dpidsRemove,
			tid,
			tradingPartnerTid,
			// Same post-trade organization the evaluation builds below: reuse its cached legal roster
			legalRosterOf: (players: Player[]) => this.virtualRoster(players),
		});

  const baseball=isSport("baseball");
  const competition=baseball?await this.getCompetitive(tid):{C:0,strength:0,record:0,strategyScore:0,window:strategy};
  const organization=baseball?await organizationPlayers(tid):[];
  const incomingPlayers:Player[]=[];
  if(baseball)for(const pid of pidsAdd){const p=await idb.cache.players.get(pid);if(p)incomingPlayers.push(p);}
  const before=baseball?this.virtualRoster(organization):undefined;
  const after=baseball?this.virtualRoster([...organization.filter(p=>!pidsRemove.includes(p.pid)),...incomingPlayers]):undefined;
  const coreRanking=organization.filter(p=>g.get("season")-p.born.year<=25)
   .sort((a,b)=>last(b.ratings).pot-last(a.ratings).pot || b.value-a.value || a.pid-b.pid);
  const fits=baseball?jointFits(add.filter(a=>a.type==='player').map(a=>a.profile),roster.filter(a=>a.type==='player').map(a=>a.profile)):new Map<number,{factor:number;reason:string}>();
  // Where the player would rather play: compares the team getting him with the team giving him away
  const affinityFactors=new Map<number,number>();
  const opportunityFactors=new Map<number,number>();
  if(baseball){
   // The seller's lack of opportunity is the same market fact for both clubs.
   for(const pid of new Set([...pidsAdd,...pidsRemove])){
    const p=await idb.cache.players.get(pid);
    const owner=p?currentTid(p):undefined;
    if(p&&owner!==undefined&&owner>=0)opportunityFactors.set(pid,(await this.getDisposition(p,owner))?.opportunityFactor??1);
   }
   const sides=[[pidsAdd,true],[pidsRemove,false]] as const;
   for(const [pids,incomingSide] of sides)for(const pid of pids){
    const p=await idb.cache.players.get(pid);
    const from=p?(incomingSide?currentTid(p):tid):undefined;
    const to=incomingSide?tid:tradingPartnerTid;
    if(!p||from===undefined||to===undefined||from<0||to<0||from===to)continue;
    const diff=(await this.getAffinity(p,to))-(await this.getAffinity(p,from));
    affinityFactors.set(pid,affinityFactor(diff,incomingSide));
   }
  }
  const describe = (assets:Asset[],incoming:boolean) => {
   const rows=assets.map(asset=>{
    const talent=sumValues([{...asset, value:asset.type==='player'?asset.talentValue:asset.value,contractValue:0}],"neutral",tid,false);
    // Remove legacy strategy multipliers. Keep existing quality exponent, salary economics and difficulty.
    const objective=sumValues([asset],baseball?"neutral":strategy,tid,true);
    const noSalary=sumValues([{...asset,contractValue:0}],baseball?"neutral":strategy,tid,true);
    const control=asset.type==='player'?asset.control:undefined;
    const controlFactor=objective>0?(control?.controlFactor??1):1;
    const moodFactor=!incoming&&objective>0?(control?.moodFactor??1):1;
    const rank=asset.type==='player'?coreRanking.findIndex(p=>p.pid===asset.pid)+1:0;
    const core=asset.type==='player'&&rank>0?coreScore(asset.age,Math.max(0,...Object.values(asset.profile.pots)),rank,control?.yearsRemaining??0):0;
    const holdingFactor=baseball&&!incoming&&objective>0?holdMultiplier(competition.C,core):1;
    const fit=incoming&&asset.type==='player'?fits.get(asset.pid):undefined;
    const fitFactor=objective>0?(fit?.factor??1):1;
    // Contenders discount young incoming players by how much of their value is future, not current help (A4)
    const prospectFactor=baseball&&incoming&&asset.type==='player'&&objective>0?contenderProspectFactor(competition.C,asset.age):1;
    const traitFactor=asset.type==='player'&&objective>0?(affinityFactors.get(asset.pid)??1):1;
    const opportunityFactor=baseball&&asset.type==='player'&&objective>0?(opportunityFactors.get(asset.pid)??1):1;
    return {type:asset.type,id:asset.type==='player'?asset.pid:asset.dp.dpid,
     name:asset.type==='player'?asset.name:`${asset.dp.season} round ${asset.dp.round} (${g.get("teamInfoCache")[asset.dp.originalTid]?.abbrev??asset.dp.originalTid})`,
     objective,talent,strategyAdjustment:noSalary-talent,salaryAdjustment:objective-noSalary,
     control,controlFactor,moodFactor,holdingFactor,traitFactor,opportunityFactor,coreScore:core,fitFactor,prospectFactor,packageFactor:1,
     reason:fit?.reason??(asset.type==='pick'?'Estimated draft position and year':'Talent and contractual control'),
     marketValue:objective*controlFactor*fitFactor*opportunityFactor,
     value:objective*controlFactor*fitFactor*moodFactor*holdingFactor*prospectFactor*traitFactor*opportunityFactor};
   });
   return rows;
  };

		const incoming = describe(add, true);
		const outgoing = describe(remove, false);
  // Minor leaguers received close deals that are almost there: they add their floor straight to the total, outside
  // the portfolio (which shrinks small pieces) and the redundancy check
  const fillerIds=new Set<number>();
  if(baseball){
   const minors=incoming.filter(r=>r.type==='player'&&r.opportunityFactor===1&&incomingPlayers.some(p=>p.pid===r.id&&p.tid===PLAYER.MINORS));
   const biggest=Math.max(0,...incoming.map(r=>r.value),...outgoing.map(r=>r.value));
   const floors=fillerFloors(minors.map(r=>r.value),biggest);
   minors.forEach((row,i)=>{
    if(!floors.fillers.has(i))return;
    fillerIds.add(row.id);row.value=floors.values[i]!;row.reason='Minor leaguer: helps close the deal';
   });
  }
  const aggregate=(allRows:typeof incoming,assets:Asset[],incomingSide:boolean)=>{
   const isFiller=(r:typeof incoming[number])=>incomingSide&&r.type==='player'&&fillerIds.has(r.id);
   const fillerTotal=allRows.filter(isFiller).reduce((s,r)=>s+r.value,0);
   const rows=allRows.filter(r=>!isFiller(r));
   const pv=rows.filter(r=>r.type==='player').map(r=>r.value);
   const picks=rows.filter(r=>r.type==='pick').map(r=>r.value);
   const redundant=baseball&&incomingSide?redundancy(rows.flatMap(row=>{
    const a=assets.find(a=>a.type==='player'&&a.pid===row.id);
    return a?.type==='player'?[{value:row.value,profile:a.profile,fitFactor:row.fitFactor}]:[];
   })):{penalty:0,concentration:0};
   const players=baseball?portfolio(pv,1.10):pv.reduce((s,v)=>s+v,0);
   const draftPicks=baseball?portfolio(picks,1.05):picks.reduce((s,v)=>s+v,0);
   // Euler allocation: per-asset contributions add exactly to the displayed portfolio.
   for(const type of ['player','pick'] as const){const selected=rows.filter(r=>r.type===type);
    const weights=baseball?packageWeights(selected.map(r=>r.value),type==='player'?1.10:1.05):selected.map(()=>1);
    selected.forEach((row,i)=>{row.packageFactor=weights[i]!*(type==='player'&&row.value>0?1-redundant.penalty:1);row.value*=row.packageFactor;});
   }
   const positivePlayers=players-pv.filter(v=>v<0).reduce((s,v)=>s+v,0);
   return {players:players+fillerTotal,picks:draftPicks,redundancy:redundant.penalty,concentration:redundant.concentration,
    total:players+fillerTotal+draftPicks-positivePlayers*redundant.penalty};
  };
  const receivedPortfolio=aggregate(incoming,add,true);
  const givenPortfolio=aggregate(outgoing,remove,false);
  const deltaTeamOvr=(after?.ovr??0)-(before?.ovr??0);
  const impact=baseball?immediateImpact(competition.C,deltaTeamOvr):0;
  const received=receivedPortfolio.total+impact;
  const given=givenPortfolio.total;
  return {tid,strategy,incoming,outgoing,received,given,net:received-given,
   competition,receivedPortfolio,givenPortfolio,immediateImpact:impact,deltaTeamOvr,before,after};
	}
}

const getModifiedPickRank = async (
	cache: ValueChangeCache,
	tid: number,
	pidsAdd: number[],
	pidsRemove: number[],
	// The calculator's cached legal roster: the same post-trade organization is computed there for the evaluation
	legalRosterOf: (players: Player[]) => { ovr: number } = legalRoster,
) => {
	// later we need to find the new ranks of this team's ovr/estimated win%
	// it's cleaner to determine this by temporarily removing the old team info from the cached lists
	const newTeamOvrs = cache.teamOvrs.filter((t) => t.tid !== tid);
	const newWps = cache.wps.filter((w) => w.tid !== tid);

	const teamSeason = await idb.cache.teamSeasons.indexGet(
		"teamSeasonsBySeasonTid",
		[g.get("season"), tid],
	);
	const gp = teamSeason ? helpers.getTeamSeasonGp(teamSeason) : 0;
	const seasonFraction = gp / g.get("numGames");

	const players = isSport("baseball") ? await organizationPlayers(tid) : await idb.cache.players.indexGetAll("playersByTid", tid);
	const playersAfterTrade = players.filter((p) => !pidsRemove.includes(p.pid));
	for (const pid of pidsAdd) {
		const p = await idb.cache.players.get(pid);
		if (p && (p.tid >= 0 || isSport("baseball"))) {
			playersAfterTrade.push(p);
		}
	}
	const playerRatings = playersAfterTrade.map((p) => ({
		pid: p.pid,
		injury: p.injury,
		value: p.value,
		ratings: {
			ovr: last(p.ratings).ovr,
			ovrs: last(p.ratings).ovrs,
			pos: last(p.ratings).pos,
		},
	}));

	const newTeamOvr = isSport("baseball") ? legalRosterOf(playersAfterTrade).ovr : team.ovr(playerRatings);
	let newTeamOvrIndex = newTeamOvrs.findIndex((t) => t.ovr < newTeamOvr);
	if (newTeamOvrIndex === -1) {
		// Worst Team (no -1 because we already removed this team from newTeamOvrs)
		newTeamOvrIndex = newTeamOvrs.length;
	}

	const newTeamOvrWinp = ovrIndexToEstWinPercent(newTeamOvrIndex);
	const newWp =
		gp === 0
			? newTeamOvrWinp
			: seasonFraction * ((teamSeason?.won ?? 0) / gp) +
				(1 - seasonFraction) * newTeamOvrWinp;

	let newRank = newWps.findIndex((w) => newWp < w.wp);
	if (newRank === -1) {
		// Best Team (no -1 because we already removed this team from newTeamOvrs)
		newRank = newWps.length;
	}
	newRank += 1; // Index to rank

	return newRank;
};

